import { UserPermissions, UserType } from "healthex-common/user.dtos";
import { indexBy } from "healthex-common/utils";
import { NamedRoutes } from "./NamedRoutes";
const anyOrgAdmin = [
    { userType: UserType.HEALTHCARE_ORGANIZATION, permissions: [UserPermissions.ORG_ADMIN] },
    { userType: UserType.EXTERNAL_RESEARCHER, permissions: [UserPermissions.ORG_ADMIN] },
    { userType: UserType.SERVICE_PROVIDER, permissions: [UserPermissions.ORG_ADMIN] },
];
const routeAuthConfig = [
    { route: NamedRoutes.HEALTHCARE_ORG_ANALYTICS, restrictedTo: [UserType.HEALTHCARE_ORGANIZATION, UserType.SERVICE_PROVIDER] },
    {
        route: NamedRoutes.PROJECTS,
        restrictedTo: [UserType.EXTERNAL_RESEARCHER, UserType.HEALTHCARE_ORGANIZATION],
    },
    { route: NamedRoutes.PATIENT_HOME_ONBOARDING, restrictedTo: [UserType.PATIENT, UserType.SCREENSHOT_USER] },
    { route: NamedRoutes.PATIENT_HOME, restrictedTo: [UserType.PATIENT, UserType.SCREENSHOT_USER] },
    { route: NamedRoutes.PATIENT_MY_HEALTHEX_LEGACY_REDIRECT, restrictedTo: [UserType.PATIENT, UserType.SCREENSHOT_USER] },
    { route: NamedRoutes.PATIENT_SHARING, restrictedTo: [UserType.PATIENT] },
    { route: NamedRoutes.PATIENT_CREATE_SMART_LINK, restrictedTo: [UserType.PATIENT] },
    { route: NamedRoutes.PATIENT_SMART_LINK_DETAILS, restrictedTo: [UserType.PATIENT] },
    {
        route: NamedRoutes.GLOBAL_ADMIN,
        restrictedTo: [{ userType: UserType.SERVICE_PROVIDER, permissions: [UserPermissions.GLOBAL_PROVISIONING] }],
    },
    {
        route: NamedRoutes.PROJECTS_PROJECT_MANAGER,
        restrictedTo: [
            { userType: UserType.HEALTHCARE_ORGANIZATION, permissions: [UserPermissions.RESEARCHER] },
            { userType: UserType.EXTERNAL_RESEARCHER, permissions: [UserPermissions.RESEARCHER] },
        ],
    },
    { route: NamedRoutes.ADMIN_ORG_SETTINGS, restrictedTo: anyOrgAdmin },
    { route: NamedRoutes.ADMIN_USERS, restrictedTo: anyOrgAdmin },
    {
        route: NamedRoutes.ADMIN_DATA_SETTINGS,
        restrictedTo: [
            { userType: UserType.HEALTHCARE_ORGANIZATION, permissions: [UserPermissions.ORG_ADMIN, UserPermissions.RESEARCH_ADMIN] },
            { userType: UserType.EXTERNAL_RESEARCHER, permissions: [UserPermissions.ORG_ADMIN, UserPermissions.RESEARCH_ADMIN] },
        ],
    },
    {
        route: NamedRoutes.ADMIN_RESEARCH_SETTINGS,
        restrictedTo: [
            { userType: UserType.HEALTHCARE_ORGANIZATION, permissions: [UserPermissions.RESEARCH_ADMIN] },
            { userType: UserType.EXTERNAL_RESEARCHER, permissions: [UserPermissions.ORG_ADMIN] },
        ],
    },
    {
        route: NamedRoutes.ADMIN_PATIENT_SETTINGS,
        restrictedTo: [{ userType: UserType.HEALTHCARE_ORGANIZATION, permissions: [UserPermissions.RESEARCH_ADMIN] }],
    },
    { route: NamedRoutes.ADMIN_APIKEYS, restrictedTo: anyOrgAdmin },
    {
        route: NamedRoutes.PATIENT_CONSENT_ENROLLMENT,
        restrictedTo: [UserType.PATIENT, UserType.SCREENSHOT_USER],
    },
    {
        route: NamedRoutes.GUIDED_PATIENT_CONSENT_ENROLLMENT,
        restrictedTo: [UserType.SCREENSHOT_USER, { userType: UserType.HEALTHCARE_ORGANIZATION, permissions: [UserPermissions.PROVIDER] }],
    },
    { route: NamedRoutes.WELCOME_ORG_USER_DETAILS, restrictedTo: [UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER] },
    { route: NamedRoutes.WELCOME_BASIC_ORG_DETAILS, restrictedTo: [UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER] },
    { route: NamedRoutes.WELCOME_CUSTOMIZE_ORG_DETAILS, restrictedTo: [UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER] },
];
const hostedSsoAllowedRouteNames = new Set([
    NamedRoutes.PATIENT_CONSENT_ENROLLMENT.routeName,
    NamedRoutes.PATIENT_CONSENT_DATA_UPDATE.routeName,
    NamedRoutes.PATIENT_CONSENT_RESUME.routeName,
    NamedRoutes.PATIENT_CONSENT_IDP_CALLBACK.routeName,
    NamedRoutes.LOGIN_AS_PATIENT.routeName,
    NamedRoutes.CLEAR_IAL2_CALLBACK.routeName,
    NamedRoutes.FACILITATED_FHIR_CALLBACK.routeName,
    NamedRoutes.FACILITATED_FHIR_OPEN_EPIC_CALLBACK.routeName,
    NamedRoutes.FASTEN_CALLBACK.routeName,
]);
export class RoutingAuthUIManager {
    constructor(authManager, routingUIManager, persistence) {
        this.authManager = authManager;
        this.routingUIManager = routingUIManager;
        this.persistence = persistence;
        this.routeIndex = indexBy(routeAuthConfig, (item) => item.route.routeName);
        this.routingUIManager.registerRedirectInterceptor((route) => {
            if (this.authManager.isHostedSsoRestricted && !this.isHostedSsoAllowed(route)) {
                console.log(`[HostedFlow] Restricted session denied for route ${route.routeName}; clearing hosted-flow sessionStorage, logging out, redirecting to LOGIN_AS_PATIENT.`);
                this.persistence.clearHostedFlowSessionState();
                void this.authManager.logout(true);
                return NamedRoutes.LOGIN_AS_PATIENT;
            }
            if (!this.isRouteAuthorized(route)) {
                console.log(`User is not authorized for route ${route.routeName}, redirecting`);
                return NamedRoutes.INDEX;
            }
            return null;
        });
    }
    isHostedSsoAllowed(route) {
        let cursor = route;
        while (cursor) {
            if (hostedSsoAllowedRouteNames.has(cursor.routeName))
                return true;
            cursor = cursor.parent;
        }
        return false;
    }
    isRouteAuthorized(route) {
        const routeRestrictions = this.findRelevantRouteRestrictions(route);
        if (!routeRestrictions) {
            return true;
        }
        if (!this.authManager.userType) {
            return true;
        }
        return routeRestrictions.some((restriction) => {
            if (typeof restriction === "object") {
                return (restriction.userType === this.authManager.userType &&
                    restriction.permissions.some((requiredPermission) => this.authManager.permissions.includes(requiredPermission)));
            }
            else {
                return restriction === this.authManager.userType;
            }
        });
    }
    findRelevantRouteRestrictions(route) {
        const exactMatch = this.routeIndex[route.routeName];
        if (exactMatch) {
            return exactMatch.restrictedTo;
        }
        else if (route.parent) {
            return this.findRelevantRouteRestrictions(route.parent);
        }
        else {
            return null;
        }
    }
}
